import { ResizeDirections } from "@/scripts/common/enums.js";
import {
    clamp,
    cloneAspectRatioPresets,
    mergeOptions,
    simplifyRatio,
} from "@/scripts/directives/utils/resizeHelpers.js";

// TEMP: Hide resize handles until module list datafix completes
const TEMP_DISABLE_RESIZE_HANDLES = false;
const ENABLE_RESIZE_QUERY_PARAM = "enableResize";

const isResizeAllowed = () => {
    if (!TEMP_DISABLE_RESIZE_HANDLES) return true;
    if (typeof window === "undefined" || !window.location) return false;

    const searchParams = new URLSearchParams(window.location.search);
    const overrideValue = searchParams.get(ENABLE_RESIZE_QUERY_PARAM);
    if (overrideValue === null) return false;
    return ["", "1", "true", "yes", "on"].includes(overrideValue.toLowerCase?.() ?? "");
};

class ResizableController {
    constructor(el, bindingValue = {}) {
        this.el = el;
        this.options = mergeOptions(bindingValue);
        this.state = {
            container: null,
            handles: {},
            spacerNode: null,
            boundHandles: new WeakSet(),
            isActive: false,
            isResizing: false,
            resizingSide: null,
            switchedToBleedThisDrag: false,
            prevCols: { start: null, end: null },
            columns: { start: null, end: null },
            aspectRatio: null,
            popularAspectRatios: cloneAspectRatioPresets(),
            size: {
                height: null,
            },
            dragStart: {
                x: 0,
                y: 0,
                column: 1,
                width: 0,
                height: 0,
                mediaHeight: 0,
            },
        };

        this.onMouseMoveBound = (e) => this.onMouseMove(e);
        this.onMouseUpBound = () => this.onMouseUp();
    }

    initialize() {
        this.state.container = this.el.closest(".grid");
        if (!this.state.container) {
            console.warn("v-resizable: parent .grid container not found");
            return false;
        }

        this.applyInitialStyles();
        this.buildHandles();
        this.bindHandleEvents();
        this.wireSpacerHandle();
        this.setFullBleed(this.options.isFullBleed);
        this.setActive(this.options.enabled);
        this.setAspectRatioOnMedia();

        return true;
    }

    updateOptions(next = {}, prev = {}) {
        if (!next) return;

        if (next.isFullBleed !== prev.isFullBleed) {
            this.setFullBleed(!!next.isFullBleed);
        }

        if (next.enabled !== prev.enabled) {
            this.setActive(!!next.enabled);
        }

        if (next.isLocked !== prev.isLocked) {
            this.options.isLocked = !!next.isLocked;
            if (this.options.isLocked) {
                this.setActive(false);
            } else if (next.enabled !== undefined) {
                this.setActive(!!next.enabled);
            }
            this.updateHandleVisibility();
        }

        if (next.useSpacers !== prev.useSpacers) {
            this.options.useSpacers = next.useSpacers !== false;
            this.wireSpacerHandle();
            this.updateHandleVisibility();
        }

        let needsSync = false;

        if (next.columnStart !== prev.columnStart) {
            needsSync = this.setColumnStartOnly(next.columnStart ?? this.getColumnStart()) || needsSync;
        }

        if (next.columnEnd !== prev.columnEnd) {
            const fallbackEnd =
                next.columnStart != null
                    ? (next.columnStart + (next.minColumnSpan ?? this.options.minColumnSpan))
                    : this.getColumnEnd();
            needsSync = this.setColumnEndOnly(next.columnEnd ?? fallbackEnd) || needsSync;
        }

        if (next.height !== prev.height) {
            if (next.height != null) {
                this.setHeight(next.height);
            }
            needsSync = true;
        }

        if (needsSync) {
            this.syncMediaFromDom();
        }

        if (next.resizeDirections !== prev.resizeDirections) {
            this.setResizeDirections(next.resizeDirections);
        }

        this.assignDefinedOptions(next);
        this.wireSpacerHandle();
    }

    cleanup() {
        Object.values(this.state.handles).forEach((handle) => {
            if (!handle) return;
            if (handle.classList?.contains("rs-handle")) {
                handle.remove();
            }
        });
        if (this.state.spacerNode) {
            delete this.state.spacerNode.__rsBound;
        }
        document.removeEventListener("mousemove", this.onMouseMoveBound);
        document.removeEventListener("mouseup", this.onMouseUpBound);
    }

    getPublicApi() {
        return {
            setActive: (state) => this.setActive(state),
            setHeight: (height, newMediaSize) => this.setHeight(height, newMediaSize),
            setMediaSize: (containerHeight) => this.setMediaSize(containerHeight),
            applyNewMediaAspectRatio: (w, h) => this.applyNewMediaAspectRatio(w, h),
            storeMediaAspectRatio: (w, h) => this.storeMediaAspectRatio(w, h),
            emitMediaResize: (w, h) => this.emitMediaResize(w, h),
            setResizeDirections: (dir) => this.setResizeDirections(dir),
            isResizing: () => this.state.isResizing,
            mediaAspectRatio: () => this.options.mediaAspectRatio,
            setFullBleed: (next) => this.setFullBleed(next),
            cleanup: () => this.cleanup(),
        };
    }

    normalizeColumnValue(value, fallback = 1) {
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return Math.round(numeric);
        const fallbackNumeric = Number(fallback);
        return Number.isFinite(fallbackNumeric) ? Math.round(fallbackNumeric) : 1;
    }

    getColumnStart() {
        if (this.state.columns.start != null) return this.state.columns.start;
        return this.normalizeColumnValue(this.options.columnStart, 1);
    }

    getColumnEnd() {
        if (this.state.columns.end != null) return this.state.columns.end;
        const fallbackStart = this.getColumnStart();
        return this.normalizeColumnValue(
            this.options.columnEnd,
            fallbackStart + this.options.minColumnSpan
        );
    }

    getColumnSpan() {
        return this.getColumnEnd() - this.getColumnStart();
    }

    setColumns(start, end) {
        const normalizedStart = clamp(
            this.normalizeColumnValue(start, 1),
            1,
            this.options.columns + 1 - this.options.minColumnSpan
        );
        const minEnd = normalizedStart + this.options.minColumnSpan;
        const normalizedEnd = clamp(
            this.normalizeColumnValue(end, minEnd),
            minEnd,
            this.options.columns + 1
        );

        const changed =
            normalizedStart !== this.state.columns.start ||
            normalizedEnd !== this.state.columns.end;

        this.state.columns.start = normalizedStart;
        this.state.columns.end = normalizedEnd;
        this.options.columnStart = normalizedStart;
        this.options.columnEnd = normalizedEnd;
        return changed;
    }

    setColumnStartOnly(start) {
        return this.setColumns(start, this.getColumnEnd());
    }

    setColumnEndOnly(end) {
        return this.setColumns(this.getColumnStart(), end);
    }

    assignDefinedOptions(delta = {}) {
        Object.entries(delta).forEach(([key, value]) => {
            if (value === undefined) return;
            this.options[key] = value;
        });
    }

    setStoredHeight(value) {
        this.state.size.height = value ?? null;
        this.options.height = this.state.size.height;
    }

    getStoredHeight() {
        return this.state.size.height;
    }

    getDomHeight() {
        const rectHeight = this.el.getBoundingClientRect?.().height;
        if (rectHeight) return rectHeight;

        const offsetHeight = this.el.offsetHeight;
        if (offsetHeight) return offsetHeight;

        const style = getComputedStyle(this.el);
        const parsed = parseFloat(style.height);
        if (!Number.isNaN(parsed) && parsed > 0) return parsed;

        return this.getFirstChildHeight();
    }

    getEffectiveHeight() {
        return this.getStoredHeight() ?? this.getDomHeight();
    }

    applyInitialStyles() {
        const { columnStart, columnEnd, height } = this.options;
        this.setColumns(
            columnStart ?? 1,
            columnEnd ?? (columnStart ?? 1) + this.options.minColumnSpan
        );
        if (height !== null) this.setHeight(height);
    }

    buildHandles() {
        const createHandle = (position, cursor, extraStyles = {}) => {
            const handle = document.createElement("div");
            handle.classList.add("rs-handle", `rsh-${position}`);
            Object.assign(handle.style, {
                display: "none",
                cursor,
                ...extraStyles,
            });
            const targetElement = this.el.querySelector('[class*="cts-module-"]');
            if (targetElement) {
                targetElement.appendChild(handle);
            } else {
                this.el.appendChild(handle);
            }
            return handle;
        };

        const handleFactories = {
            top: () => createHandle("tm", "ns-resize"),
            bottom: () => createHandle("bm", "ns-resize"),
            left: () => createHandle("ml", "ew-resize"),
            right: () => createHandle("mr", "ew-resize"),
            "top-left": () => createHandle("tl", "nwse-resize"),
            "top-right": () => createHandle("tr", "nesw-resize"),
            "bottom-left": () => createHandle("bl", "nesw-resize"),
            "bottom-right": () => createHandle("br", "nwse-resize"),
        };

        this.state.handles = Object.fromEntries(
            Object.entries(handleFactories).map(([key, factory]) => [key, factory()])
        );

        const spacerHandle = this.el.querySelector(".spacer-resize-handle") || null;
        if (spacerHandle) {
            this.assignSpacerHandle(spacerHandle);
        } else {
            this.state.spacerNode = null;
        }
    }

    assignSpacerHandle(spacerHandle) {
        if (!this.spacersEnabled()) return;
        if (!spacerHandle) return;

        if (this.options.resizeDirections === ResizeDirections.SINGLE) {
            const useRightSide = this.getColumnEnd() < (this.options.columns + 1);
            const removeGeneratedHandle = (key) => {
                const handle = this.state.handles[key];
                if (handle?.classList?.contains("rs-handle")) {
                    handle.remove();
                }
            };

            if (useRightSide) {
                removeGeneratedHandle("right");
                this.state.handles.right = spacerHandle;
            } else {
                removeGeneratedHandle("left");
                this.state.handles.left = spacerHandle;
                delete this.state.handles.right;
            }
            spacerHandle.style.display = "none";
        }

        this.state.spacerNode = spacerHandle;
    }

    bindHandleEvents() {
        Object.entries(this.state.handles).forEach(([side, handle]) => {
            if (!handle || this.state.boundHandles.has(handle)) return;
            handle.addEventListener("mousedown", (event) => this.onMouseDown(event, side));
            this.state.boundHandles.add(handle);
        });
    }

    wireSpacerHandle() {
        if (!this.spacersEnabled()) {
            if (this.state.spacerNode) {
                this.state.spacerNode.style.display = "none";
            }
            this.state.spacerNode = null;
            return;
        }
        const spacerNode = this.el.querySelector(".spacer-resize-handle") || null;
        if (!spacerNode) {
            this.state.spacerNode = null;
            return;
        }

        const changedNode = spacerNode !== this.state.spacerNode;
        if (changedNode || !this.state.spacerNode) {
            this.assignSpacerHandle(spacerNode);
        }

        if (spacerNode.__rsBound) {
            if (changedNode) {
                this.updateHandleVisibility();
            }
            return;
        }

        spacerNode.style.userSelect = "none";
        spacerNode.addEventListener("mousedown", (event) => {
            const side =
                this.options.resizeDirections === ResizeDirections.SINGLE
                    ? this.singleSideForSpacer()
                    : this.pickSideFromEvent(event);
            this.onMouseDown(event, side);
        });
        spacerNode.__rsBound = true;
        this.updateHandleVisibility();
    }

    pickSideFromEvent(event) {
        const rect = this.el.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        return event.clientX >= midX ? "right" : "left";
    }

    singleSideForSpacer() {
        const end = this.getColumnEnd();
        return end < (this.options.columns + 1) ? "right" : "left";
    }

    spacersEnabled() {
        return this.options.useSpacers !== false;
    }

    setFullBleed(next) {
        const enabled = !!next;
        this.options.isFullBleed = enabled;
        //this.el.classList.toggle("is-fullbleed", enabled);
    }

    setActive(state) {
        if (this.options.isLocked) {
            if (this.state.isActive) {
                this.state.isActive = false;
                this.updateHandleVisibility();
                this.options.onActivate?.(this.state.isActive);
            }
            return;
        }
        if (this.state.isActive === state) return;
        this.state.isActive = !!state;
        this.updateHandleVisibility();
        this.options.onActivate?.(this.state.isActive);
    }

    updateHandleVisibility() {
        if (this.options.isLocked) {
            Object.values(this.state.handles).forEach((node) => {
                if (!node) return;
                node.style.display = "none";
            });
            if (this.state.spacerNode) {
                this.state.spacerNode.style.display = "none";
            }
            this.el.classList.remove("has-all-handles");
            return;
        }
        if (!isResizeAllowed()) {
            Object.values(this.state.handles).forEach((node) => {
                if (!node) return;
                node.style.display = "none";
            });

            if (this.state.spacerNode) {
                this.state.spacerNode.style.display = "none";
            }

            this.el.classList.remove("has-all-handles");
            return;
        }

        const spacersEnabled = this.spacersEnabled();
        const wanted = new Set(
            (() => {
                switch (this.options.resizeDirections) {
                    case ResizeDirections.VERTICAL:
                        return ["top", "bottom"];
                    case ResizeDirections.HORIZONTAL:
                    case ResizeDirections.SINGLE:
                        return ["left", "right"];
                    // case ResizeDirections.SINGLE:
                    //     return [this.singleSideForSpacer()];
                    case ResizeDirections.ALL:
                    default:
                        return [
                            "top",
                            "right",
                            "bottom",
                            "left",
                            "top-left",
                            "top-right",
                            "bottom-right",
                            "bottom-left",
                        ];
                }
            })()
        );

        Object.entries(this.state.handles).forEach(([pos, node]) => {
            if (!node) return;
            const visible = this.state.isActive && wanted.has(pos);
            node.style.display = visible ? "" : "none";

            if (pos === "top-left") {
                this.el.classList.toggle("has-all-handles", visible);
            }
        });

        if (this.state.spacerNode) {
            const shouldShow =
                spacersEnabled &&
                this.state.isActive &&
                this.options.resizeDirections === ResizeDirections.SINGLE;
            this.state.spacerNode.style.display = shouldShow ? "" : "none";
        }
    }

    setResizeDirections(direction) {
        if (!direction) return;
        this.options.resizeDirections = direction;
        this.updateHandleVisibility();
        this.wireSpacerHandle();
    }

    // Rects and pointer deltas are in zoomed (visual) pixels, CSS paddings and gaps are not: scale those to match.
    getZoom() {
        return this.state.container?.currentCSSZoom || 1;
    }

    getContainerWidth() {
        const style = getComputedStyle(this.state.container);
        const width = this.state.container.getBoundingClientRect().width;
        const zoom = this.getZoom();
        return (
            width -
            (parseFloat(style.paddingLeft) || 0) * zoom -
            (parseFloat(style.paddingRight) || 0) * zoom
        );
    }

    getGridSizes() {
        const totalWidth = this.getContainerWidth();
        const gap = this.options.columnGap * this.getZoom();
        const usableWidth = totalWidth - gap * (this.options.columns - 1);
        return {
            columnWidth: usableWidth / this.options.columns,
            gap,
        };
    }

    getFirstChildHeight() {
        const firstChild = this.el.firstElementChild;
        if (!firstChild) return 0;

        const style = getComputedStyle(firstChild);
        return (
            firstChild.getBoundingClientRect().height ||
            parseFloat(style.height) ||
            0
        );
    }

    setHeight(height, newMediaSize) {
        if (this.options.autoHeight || newMediaSize || this.options.isMediaModule) {
            this.setStoredHeight(null);
            return;
        }

        if (height !== "auto" && (height == null || Number.isNaN(height))) {
            height = this.getFirstChildHeight();
        }

        height = clamp(height, 0, this.options.maxHeight ?? undefined);
        this.setStoredHeight(height);
    }

    setMediaSize(containerHeight = null) {
        const media = this.el.querySelector(".media");
        const mediaCaption = this.el.querySelector(".media-caption");
        if (!media) return;

        if (this.options.keepMediaSize) {
            if (this.options.mediaAspectRatio) {
                media.style.aspectRatio = `${this.options.mediaAspectRatio[0]} / ${this.options.mediaAspectRatio[1]}`;
            }
            return { width: media.offsetWidth, height: media.offsetHeight };
        }

        if (containerHeight) {
            const captionHeight = mediaCaption ? mediaCaption.offsetHeight + 4 : 0;
            const nextHeight = Math.max(containerHeight - captionHeight, 0);
            media.style.height = `${nextHeight}px`;
            media.style.aspectRatio = `${media.offsetWidth} / ${Math.max(media.offsetHeight, 1)}`;
            this.options.mediaAspectRatio = simplifyRatio(media.offsetWidth, media.offsetHeight);
        } else {
            if (this.options.mediaAspectRatio) {
                media.style.aspectRatio = `${this.options.mediaAspectRatio[0]} / ${this.options.mediaAspectRatio[1]}`;
            }
            media.style.height = "auto";
        }

        return { width: media.offsetWidth, height: media.offsetHeight };
    }

    setAspectRatioOnMedia() {
        const media = this.el.querySelector(".media");
        if (!media || media.style.aspectRatio || !this.options.mediaAspectRatio) return;
        media.style.aspectRatio = `${this.options.mediaAspectRatio[0]} / ${this.options.mediaAspectRatio[1]}`;
    }

    storeMediaAspectRatio(width, height) {
        this.options.mediaAspectRatio = simplifyRatio(width, height);
    }

    emitResize() {
        const media = this.el.querySelector(".media");
        const columnStart = this.getColumnStart();
        const columnEnd = this.getColumnEnd();
        const height =
            this.options.autoHeight || this.options.isMediaModule
                ? null
                : this.getEffectiveHeight();
        const mediaHeight = media?.offsetHeight || null;
        const resizeDirection = this.normalizeResizeDirection(this.state.resizingSide);

        this.options.onResize?.({
            columnStart,
            columnEnd,
            height,
            mediaHeight,
            resizeDirection,
            mediaAspectRatio: this.options.mediaAspectRatio,
        });
    }

    normalizeResizeDirection(direction) {
        if (!direction) return "";
        if (direction === "left" || direction === "right" || direction === "top" || direction === "bottom") {
            return direction;
        }
        if (direction.includes("right")) return "right";
        if (direction.includes("left")) return "left";
        if (direction.includes("top")) return "top";
        if (direction.includes("bottom")) return "bottom";
        return direction;
    }

    emitMediaResize(width, height) {
        if (this.options.keepMediaSize) return;
        this.options.onMediaResize?.({
            width,
            height,
            mediaAspectRatio: this.options.mediaAspectRatio,
        });
    }

    applyNewMediaAspectRatio(croppedWidth, croppedHeight) {
        const media = this.el.querySelector(".media");
        const mediaCaption = this.el.querySelector(".media-caption");
        if (!media) return;

        const containerWidth = this.el.offsetWidth;
        const newImageAspectRatio = croppedWidth / croppedHeight;
        const imageHeight = containerWidth / newImageAspectRatio;
        const captionHeight = mediaCaption ? mediaCaption.offsetHeight : 0;
        const newContainerHeight = imageHeight + captionHeight;

        this.state.aspectRatio = containerWidth / newContainerHeight;
        this.storeMediaAspectRatio(croppedWidth, croppedHeight);
        this.options.autoHeight = false;

        const newMediaSize = this.setMediaSize();
        this.setHeight(newContainerHeight, newMediaSize);
        this.emitResize();
    }

    syncMediaFromDom() {
        const newMediaSize = this.setMediaSize();
        this.setHeight(this.getEffectiveHeight());
        if (newMediaSize) {
            this.emitMediaResize(newMediaSize.width, newMediaSize.height);
        }
    }

    rememberCurrentColumns() {
        this.state.prevCols = {
            start: this.getColumnStart(),
            end: this.getColumnEnd(),
        };
    }

    onMouseDown(event, side) {
        if (this.options.isLocked) return;
        if (event.button !== 0) return;
        event.preventDefault();
        this.state.isResizing = true;
        this.state.switchedToBleedThisDrag = false;

        this.options.onResizeStart?.();

        const media = this.el.querySelector(".media");
        this.state.resizingSide = side;
        this.state.dragStart = {
            x: event.clientX,
            y: event.clientY,
            column: this.getColumnStart(),
            width: this.el.offsetWidth,
            height: this.getEffectiveHeight(),
            mediaHeight: media?.offsetHeight ?? this.getEffectiveHeight(),
        };

        this.state.aspectRatio = this.el.offsetWidth / Math.max(this.getEffectiveHeight(), 1);
        if (media && !this.options.mediaAspectRatio) {
            this.storeMediaAspectRatio(media.offsetWidth, media.offsetHeight);
        }

        const originalPreset = this.state.popularAspectRatios.find(
            (preset) => preset.ratio === "Original"
        );
        if (originalPreset) {
            originalPreset.value = [
                media?.naturalWidth || media?.offsetWidth || 1,
                media?.naturalHeight || media?.offsetHeight || 1,
            ];
        }

        document.body.classList.add("force-resize-cursor");
        document.addEventListener("mousemove", this.onMouseMoveBound);
        document.addEventListener("mouseup", this.onMouseUpBound);
    }

    onMouseMove(event) {
        if (!this.state.isResizing || !this.state.resizingSide) return;

        const { columnWidth, gap } = this.getGridSizes();
        const snapSize = columnWidth + gap;
        const deltaX = event.clientX - this.state.dragStart.x;
        const deltaY = event.clientY - this.state.dragStart.y;

        const currentStartCol = this.getColumnStart();
        const currentEndCol = this.getColumnEnd();

        let newStartCol = currentStartCol;
        let newEndCol = currentEndCol;
        let newWidth = this.state.dragStart.width;
        let newHeight = this.state.dragStart.height;
        let colChanged = false;

        const resizingSide = this.state.resizingSide;
        const isHorizontalOnly = resizingSide === "left" || resizingSide === "right";
        const isVerticalOnly = resizingSide === "top" || resizingSide === "bottom";
        const isDiagonal = !isHorizontalOnly && !isVerticalOnly;

        if (
            isHorizontalOnly &&
            !this.options.isFullBleed &&
            !this.state.switchedToBleedThisDrag
        ) {
            const startCol = this.getColumnStart();
            const endCol = this.getColumnEnd();
            const atLeftEdge = startCol === 1;
            const atRightEdge = endCol === this.options.columns + 1;

            const pullingOutLeft = resizingSide === "left" && deltaX < 0;
            const pullingOutRight = resizingSide === "right" && deltaX > 0;

            if ((atLeftEdge && pullingOutLeft) || (atRightEdge && pullingOutRight)) {
                if (!this.options.fullBleedEnabled) return;

                const overshoot = Math.abs(deltaX);
                if (overshoot >= (this.options.enterFullBleedPx ?? 24)) {
                    this.rememberCurrentColumns();
                    this.options.onFullBleedChange?.(true);
                    this.options.isFullBleed = true;
                    this.state.switchedToBleedThisDrag = true;
                    this.setFullBleed(true);
                    return;
                }
            }
        }

        if (isHorizontalOnly && this.options.isFullBleed) {
            const pullingInLeft = resizingSide === "left" && deltaX > 0;
            const pullingInRight = resizingSide === "right" && deltaX < 0;
            const inwardDelta = Math.abs(deltaX);

            if ((pullingInLeft || pullingInRight) && inwardDelta >= (this.options.exitFullBleedPx ?? 16)) {
                if (!this.options.fullBleedEnabled) return;

                this.options.onFullBleedChange?.(false);
                this.options.isFullBleed = false;
                this.state.switchedToBleedThisDrag = false;
                this.setFullBleed(false);

                if (this.state.prevCols.start != null && this.state.prevCols.end != null) {
                    this.setColumns(this.state.prevCols.start, this.state.prevCols.end);
                }

                this.state.dragStart.x = event.clientX;
                this.state.dragStart.width = this.el.offsetWidth;
                this.state.dragStart.column = this.getColumnStart();
                return;
            }
        }

        if (resizingSide.includes("left")) {
            const deltaColumns = Math.round(deltaX / snapSize);
            newStartCol = this.state.dragStart.column + deltaColumns;

            const maxStart = currentEndCol - this.options.minColumnSpan;
            const minStart = currentEndCol - this.options.maxColumnSpan;
            newStartCol = clamp(newStartCol, Math.max(1, minStart), maxStart);

            const startChanged = this.setColumnStartOnly(newStartCol);
            if (startChanged) {
                colChanged = true;
            }

            const span = currentEndCol - newStartCol;
            newWidth = span * snapSize - gap;
        }

        if (resizingSide.includes("right")) {
            const baseStartCol = this.state.dragStart.column;
            newEndCol = Math.min(
                baseStartCol + this.getNewSpan(this.state.dragStart.width + deltaX, gap, snapSize),
                this.options.columns + 1
            );
            const endChanged = this.setColumnEndOnly(newEndCol);
            if (endChanged) {
                colChanged = true;
            }
            const span = newEndCol - currentStartCol;
            newWidth = span * snapSize - gap;
        }

        if (isDiagonal && colChanged) {
            newHeight = newWidth / (this.state.aspectRatio || 1);
            const newMediaSize = this.setMediaSize();
            this.setHeight(newHeight, newMediaSize);
        }

        if (isVerticalOnly) {
            this.handleVerticalResize(deltaY);
        }

        if (isHorizontalOnly) {
            this.setMediaSize(this.state.dragStart.height);
            this.setHeight(this.state.dragStart.height);
        }

        this.emitResize();
    }

    getNewSpan(newWidth, gap, snapSize) {
        let newSpan = Math.round((newWidth + gap) / snapSize);
        return clamp(newSpan, this.options.minColumnSpan, this.options.maxColumnSpan);
    }

    handleVerticalResize(deltaY) {
        const media = this.el.querySelector(".media");
        if (!media) {
            this.setHeight(this.state.dragStart.height + deltaY);
            return;
        }

        const mediaCaption = this.el.querySelector(".media-caption");
        const captionHeight = mediaCaption ? mediaCaption.offsetHeight + 4 : 0;
        const resizingSide = this.state.resizingSide;
        let newMediaHeight =
            resizingSide === "top"
                ? this.state.dragStart.mediaHeight - deltaY
                : this.state.dragStart.mediaHeight + deltaY;

        const mediaWidth = media.offsetWidth;
        let snappedMediaHeight = newMediaHeight;
        let snappedRatio = null;

        this.options.autoHeight = false;

        for (const ratio of this.state.popularAspectRatios) {
            if (!ratio.value) continue;
            const targetHeight = mediaWidth / (ratio.value[0] / ratio.value[1]);
            if (!Number.isFinite(targetHeight)) continue;
            const diff = Math.abs(newMediaHeight - targetHeight);
            if (diff <= 20) {
                snappedMediaHeight = targetHeight;
                snappedRatio = ratio;
                break;
            }
        }

        const finalMediaHeight = Math.floor(snappedMediaHeight);
        media.style.height = `${finalMediaHeight}px`;

        if (snappedRatio) {
            this.options.mediaAspectRatio = [...snappedRatio.value];
            media.style.aspectRatio = `${snappedRatio.value[0]} / ${snappedRatio.value[1]}`;
            this.options.onAspectRatioSnap?.({ show: true, label: snappedRatio.ratio });
        } else {
            const w = media.offsetWidth || 1;
            const h = finalMediaHeight || 1;
            this.options.mediaAspectRatio = simplifyRatio(w, h);
            media.style.aspectRatio = `${this.options.mediaAspectRatio[0]} / ${this.options.mediaAspectRatio[1]}`;
            this.options.onAspectRatioSnap?.({ show: false });
        }

        this.setHeight(finalMediaHeight + captionHeight);
    }

    onMouseUp() {
        this.state.isResizing = false;
        this.state.resizingSide = null;
        this.options.onResizeEnd?.();
        document.body.classList.remove("force-resize-cursor");
        document.removeEventListener("mousemove", this.onMouseMoveBound);
        document.removeEventListener("mouseup", this.onMouseUpBound);
        this.options.onAspectRatioSnap?.({ show: false });
    }
}

function shouldSkipUpdate(el) {
    return el.__resizeDirectiveApi__?.isResizing?.() && !el.classList.contains("centered");
}

export default {
    mounted(el, binding) {
        const controller = new ResizableController(el, binding.value);
        if (!controller.initialize()) return;

        el.__resizableController__ = controller;
        el.__resizeDirective__ = controller.getPublicApi();
        el.__resizeDirectiveApi__ = el.__resizeDirective__;
    },
    updated(el, binding) {
        if (shouldSkipUpdate(el)) return;
        const controller = el.__resizableController__;
        if (!controller) return;
        controller.updateOptions(binding.value || {}, binding.oldValue || {});
        controller.wireSpacerHandle();
    },
    beforeUnmount(el) {
        el.__resizeDirective__?.cleanup?.();
        delete el.__resizeDirective__;
        delete el.__resizeDirectiveApi__;
        delete el.__resizableController__;
    },
};
